'use client';

import { useState, useCallback, useEffect } from 'react';
import React from 'react';
import { Col, Container, Form, Row, Spinner, Toast } from 'react-bootstrap';
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useTranslation } from '@/i18n/client';
import { Locale } from '../../../../../i18n-config';
import AppService from '@/services/appService';
import Image from 'next/image';

const CForm = ({ setShowToast, lang }: { setShowToast: (state: boolean) => void; lang: Locale }) => {
  const { t } = useTranslation(lang);
  const { executeRecaptcha } = useGoogleReCaptcha();

  const [contact, setContact] = useState({
    email: '',
    message: '',
    captcha_token: '',
  });

  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState<'idle' | 'failed' | 'success'>('idle');
  const [error, setError] = useState<any>();
  const [counter, setCounter] = useState(0);
  const [focused, setFocused] = useState(false);
  const [email, setEmail] = useState('');
  const [isRecaptchaAvailable, setIsRecaptchaAvailable] = useState(false);

  const handleChangeInputValue = (targetElem: any) => {
    setError({});

    setContact({
      ...contact,
      [targetElem.name]: targetElem.value,
    });

    setEmail(contact.email);
  };

  const renderErrors = () => {
    if (typeof error === 'object') {
      for (const key in error) {
        if (Object.prototype.hasOwnProperty.call(error, key)) {
          return (
            <li className="text-danger">
              <small>{error[key]}</small>
            </li>
          );
        }
      }
    } else {
      return (
        <li className="text-danger">
          <small>{error}</small>
        </li>
      );
    }
  };

  const handleRecaptchaVerify = useCallback(async () => {
    try {
      if (!executeRecaptcha) {
        setIsRecaptchaAvailable(false);

        return;
      }

      setIsRecaptchaAvailable(true);

      const token = await executeRecaptcha('login');

      setContact((prev) => {
        return { ...prev, captcha_token: token };
      });
    } catch (error) {
      setError({
        message: t('CForm/key2'),
      });
    }
  }, [executeRecaptcha, t]);

  const handleSubmit = async (event: React.SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();
    event.stopPropagation();

    try {
      if (counter <= 0 || (counter > 0 && contact.email !== email)) {
        if (contact.captcha_token) {
          setIsLoading(true);

          setStatus('idle');

          if (contact.email === 'info@l-k.io') {
            setStatus('failed');

            setIsLoading(false);

            setError({
              message: t('CForm/key5'),
            });

            return;
          }

          await AppService.contactUs(contact);

          setStatus('success');

          setIsLoading(false);

          setShowToast(true);

          setContact((prev) => {
            return { ...prev, email: '', message: '' };
          });

          setCounter(counter + 1);

          handleRecaptchaVerify();
        } else {
          setIsLoading(false);

          setError({
            message: t('CForm/key1'),
          });
        }
      } else {
        setStatus('failed');

        setError({
          message: t('CForm/key6'),
        });
      }
    } catch (error: any) {
      setIsLoading(false);

      setStatus('failed');

      setShowToast(false);

      if (error.response?.data?.element) setError(error.response.data.element);
      else setError(error.response?.data?.message || error.message);
    }
  };

  useEffect(() => {
    handleRecaptchaVerify();
  }, [handleRecaptchaVerify]);

  return (
    <Form onSubmit={handleSubmit}>
      <Form.Group className="mb-3" controlId="email">
        <Form.Control
          onChange={(e) => handleChangeInputValue(e.target)}
          value={contact.email}
          type="email"
          placeholder={t('CForm/key8')}
          name="email"
          required
        />
        <Form.Text className="text-muted">{t('CForm/key4')}</Form.Text>
      </Form.Group>

      <div className={`content-wrapper ${focused ? 'active' : ''}`}>
        <Form.Group className="mb-3" controlId="message">
          <Form.Control
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            style={{ resize: 'none' }}
            onChange={(e) => handleChangeInputValue(e.target)}
            value={contact.message}
            placeholder={t('CForm/key7') as string}
            name="message"
            as="textarea"
            rows={5}
            required
            minLength={10}
            maxLength={3000}
          />
        </Form.Group>

        <div
          className={`d-flex align-items-center ${
            status !== 'failed' ? 'justify-content-end' : 'justify-content-between'
          } p-3`}
        >
          {status === 'failed' && <ul className="list-unstyled p-0 m-0">{renderErrors()}</ul>}
          <button type="submit" className="lk-red-button" disabled={isLoading || !isRecaptchaAvailable}>
            {isLoading ? <Spinner animation="grow" size="sm" /> : t('CForm/key3')}
          </button>
        </div>
      </div>
    </Form>
  );
};

const ContactUsClient = ({ lang }) => {
  const { t } = useTranslation(lang);
  const [show, setShow] = useState(false);

  return (
    <GoogleReCaptchaProvider useEnterprise={true} reCaptchaKey={process.env.NEXT_PUBLIC_RECAPTCHA_KEY as string}>
      <div className="contact-us">
        <div className="title-area">
          <Container>
            <Row className="justify-content-center">
              <Col lg={9} md={12} xs={12}>
                <Row className="gx-sm-5 gx-4 gy-4 align-items-center">
                  <Col lg={4} md={4} xs={12} className="text-center">
                    <Image
                      src="https://cdn.1cdn.app/application/LUMBERJACK/contact.svg"
                      alt="contact us"
                      className="img-fluid w-100 h-100"
                      width={0}
                      height={0}
                      sizes="100vw"
                    />
                  </Col>
                  <Col lg={8} md={8} xs={12}>
                    <h1 className="fs-2">
                      {t('ContactUsPage/key1')} <br /> {t('ContactUsPage/key2')}
                    </h1>
                    <p>{t('ContactUsPage/key3')}</p>
                  </Col>
                </Row>
              </Col>
            </Row>
          </Container>
        </div>
        <Container id="captchaContainer">
          <div className="form-area py-5">
            <Row className="justify-content-center">
              <Col lg={7} md={10} xs={12}>
                <div className="d-flex flex-column align-items-center justify-content-between mb-4">
                  <Image
                    src="https://cdn.1cdn.app/application/LUMBERJACK/2208240644_email_1.svg"
                    className="img-fluid"
                    width={255}
                    height={255}
                    loading="lazy"
                    title="email"
                    alt="L-K.io"
                  />
                  <h2 className="fs-3 lk-text-red mt-4 w-75 text-center">{t('ContactUsPage/key4')}</h2>
                </div>

                <CForm setShowToast={setShow} lang={lang} />

                <Toast
                  onClose={() => setShow(false)}
                  show={show}
                  delay={3000}
                  autohide
                  style={{ position: 'fixed', left: 10, bottom: 10 }}
                >
                  <Toast.Header>
                    <strong className="me-auto text-success">{t('ContactUsPage/key5')}</strong>
                  </Toast.Header>
                  <Toast.Body>{t('ContactUsPage/key6')}</Toast.Body>
                </Toast>
              </Col>
            </Row>
          </div>
        </Container>
      </div>
    </GoogleReCaptchaProvider>
  );
};

export default ContactUsClient;
